<template>
  <input v-if="attribute_name" type="hidden" :name="attribute_name" :value="outputValue">
  <VueDatePicker
    v-model="date"
    :format="computedFormat"
    :enable-time-picker="!no_sec"
    teleport="body"
    week-start="0"
    locale="ja"
    placeholder="日付を選択"
    :auto-apply="true"
    :close-on-auto-apply="false"
    :config="{ closeOnAutoApply: false }"
    :show-now-button="true"
    now-button-label="現在を選択"
  ></VueDatePicker>
</template>

<script setup>
  import { ref, computed, watch } from 'vue';
  import VueDatePicker from '@vuepic/vue-datepicker';
  import '@vuepic/vue-datepicker/dist/main.css';
  import dayjs from 'dayjs';
  import customParseFormat from 'dayjs/plugin/customParseFormat';
  dayjs.extend(customParseFormat);

  const props = defineProps({
    modelValue: {
      type: [String, Number, Date],
      default: null,
    },
    value: {
      type: [String, Number, Date],
      default: null,
    },
    from: {
      type: [String, Number, Date],
      default: null,
    },
    attribute_name: {
      type: String,
      default: null,
    },
    no_sec: {
      type: Boolean,
      default: false,
    },
  });

  const emit = defineEmits(['update:modelValue', 'change']);

  const toMoment = (value) => {
    if (value === undefined || value === null || value === '') {
      return null;
    }

    if (value instanceof Date) {
      return dayjs(value);
    }

    if (typeof value === 'number') {
      return dayjs(value);
    }

    if (typeof value === 'string') {
      const candidates = props.no_sec
        ? ['YYYY-MM-DD']
        : ['YYYY-MM-DD HH:mm', 'YYYY-MM-DDTHH:mm', null];

      for (const format of candidates) {
        const parsed = format ? dayjs(value, format, true) : dayjs(value);
        if (parsed.isValid()) {
          return parsed;
        }
      }
    }

    return null;
  };

  const parseDate = (value) => {
    const parsedMoment = toMoment(value);
    return parsedMoment ? parsedMoment.toDate() : null;
  };

  const formatForOutput = (value) => {
    if (!value) {
      return '';
    }

    const m = dayjs.isDayjs(value) ? value : dayjs(value);
    if (!m.isValid()) {
      return '';
    }

    return props.no_sec ? m.format('YYYY-MM-DD') : m.format('YYYY-MM-DD HH:mm');
  };

  const normalizeComparable = (value) => {
    if (value instanceof Date || dayjs.isDayjs(value)) {
      return formatForOutput(value);
    }

    if (typeof value === 'string' || typeof value === 'number') {
      return formatForOutput(toMoment(value));
    }

    return '';
  };

  const currentPropValue = () => (props.modelValue !== null && props.modelValue !== undefined
    ? props.modelValue
    : (props.value !== null && props.value !== undefined ? props.value : props.from));

  const date = ref(parseDate(currentPropValue()));

  watch(
    () => currentPropValue(),
    (newVal) => {
      const parsed = parseDate(newVal);
      if (normalizeComparable(parsed) !== normalizeComparable(date.value)) {
        date.value = parsed;
      }
    }
  );

  watch(
    date,
    (newVal) => {
      const formatted = formatForOutput(newVal);
      if (formatted !== normalizeComparable(currentPropValue())) {
        emit('update:modelValue', formatted);
        emit('change', formatted);
      }
    }
  );

  const outputValue = computed(() => formatForOutput(date.value));

  // computedFormatをcomputedプロパティとして定義
  const computedFormat = computed(() => {
    // date-fns用に変更：YYYYをyyyyに変更
    return props.no_sec ? 'yyyy-MM-dd' : 'yyyy-MM-dd HH:mm';  // これを修正
  });
</script>
